/**
 * useDiscussionMessages Hook - Real-Time Discussion Messages Listener
 *
 * This React hook provides real-time synchronization of discussion messages
 * using Firestore's onSnapshot listener. Messages update automatically when
 * any user posts, providing live multi-user collaboration.
 *
 * Integration with TripContext:
 * - Uses effectiveUserId for shared trip support
 * - Messages stored under trip owner's Firestore path
 * - Handles both owned and shared trips transparently
 *
 * Features:
 * - Real-time push-based updates (no polling)
 * - Automatic message ordering (timestamp ascending by default)
 * - Loading states and error handling
 * - Pagination support with cursor-based navigation
 * - Proper cleanup to prevent memory leaks
 *
 * Usage:
 * const { messages, loading, error, hasMore, loadMore } = useDiscussionMessages(tripId, {
 *   limitCount: 50,
 *   orderDirection: 'asc',
 *   autoLoad: true
 * });
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTrip } from '@trip-planner-monorepo/shared';
import { db } from '@trip-planner-monorepo/shared';
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  startAfter,
  getDocs
} from 'firebase/firestore';

/**
 * Custom hook for real-time discussion messages
 *
 * @param {string} tripId - Trip identifier (optional if using TripContext)
 * @param {Object} options - Configuration options
 * @param {number} [options.limitCount=50] - Maximum messages per load
 * @param {string} [options.orderDirection='asc'] - Order direction ('asc' or 'desc')
 * @param {boolean} [options.autoLoad=true] - Automatically load messages on mount
 * @returns {Object} Hook state and methods
 * @returns {Array} returns.messages - Array of message objects with real-time updates
 * @returns {boolean} returns.loading - Loading state (true on initial load)
 * @returns {Object|null} returns.error - Error object if operation failed
 * @returns {boolean} returns.hasMore - Whether more messages are available
 * @returns {Function} returns.loadMore - Function to load older messages (pagination)
 */
export const useDiscussionMessages = (tripId = null, options = {}) => {
  const { effectiveUserId, selectedTripId } = useTrip();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Use provided tripId or fall back to TripContext
  const activeTripId = tripId || selectedTripId;

  // Options with defaults
  const {
    limitCount = 50,
    orderDirection = 'asc', // 'asc' = oldest first, 'desc' = newest first
    autoLoad = true
  } = options;
  // For chat-style ascending display, query newest first then reverse.
  // This keeps the newest messages visible while preserving UI order.
  const queryOrderDirection = orderDirection === 'asc' ? 'desc' : orderDirection;

  // Track unsubscribe function for cleanup
  const unsubscribeRef = useRef(null);

  /**
   * Set up real-time listener for messages
   *
   * Strategy: Use onSnapshot for latest N messages to get real-time updates.
   * For pagination (loading older messages), we use a separate get() query
   * to avoid conflicts with the live listener.
   */
  useEffect(() => {
    // Clear previous state
    setMessages([]);
    setLastDoc(null);
    setHasMore(true);
    setError(null);

    // Validation
    if (!effectiveUserId || !activeTripId || !autoLoad) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Build Firestore collection reference
      const messagesRef = collection(
        db,
        `users/${effectiveUserId}/trips/${activeTripId}/discussion_messages`
      );

      // Build query with ordering and limit
      const q = query(
        messagesRef,
        orderBy('timestamp', queryOrderDirection),
        limit(limitCount)
      );

      // Set up real-time listener
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          try {
            // Check for new AI messages in this snapshot
            const newAIMessages = snapshot.docChanges()
              .filter(change => change.type === 'added')
              .map(change => change.doc.data())
              // Support both legacy and canonical sender IDs for diagnostics.
              .filter(data => data.senderId === 'AI_AGENT' || data.senderId === 'AI_Agent');

            // Transform Firestore documents to message objects
            let messagesList = snapshot.docs.map(doc => {
              const data = doc.data();
              const normalizedText = data.text ?? data.content ?? '';
              return {
                id: doc.id,
                ...data,
                text: normalizedText,
                // Convert Firestore Timestamp to ISO string for easier handling
                // Handle null timestamps (pending server write)
                timestamp: data.timestamp?.toDate?.()?.toISOString() || null,
                // Add metadata for UI
                isPending: !data.timestamp // Message still being written to server
              };
            });
            if (queryOrderDirection === 'desc') {
              messagesList = messagesList.reverse();
            }

            // Log AI responses
            if (newAIMessages.length > 0) {
              newAIMessages.forEach(aiMsg => {
                console.log('═══════════════════════════════════════════════════════════');
                console.log('🤖 AI AGENT RESPONSE RECEIVED');
                console.log('═══════════════════════════════════════════════════════════');
                console.log('💬 AI Response:', aiMsg.text || aiMsg.content || '');
                console.log('📊 Metadata:', {
                  model: aiMsg.metadata?.model || 'Unknown',
                  toolsUsed: aiMsg.metadata?.toolsUsed || [],
                  tokensUsed: aiMsg.metadata?.tokensUsed || 'Unknown'
                });
                console.log('⏰ Timestamp:', aiMsg.timestamp?.toDate?.()?.toLocaleString() || 'Pending');
                console.log('═══════════════════════════════════════════════════════════');
              });
            }

            setMessages(messagesList);

            // Store last document for pagination
            const lastSnapshot = snapshot.docs[snapshot.docs.length - 1];
            setLastDoc(lastSnapshot);

            // Determine if more messages might exist
            // If we got fewer messages than the limit, we've reached the end
            setHasMore(snapshot.docs.length === limitCount);

            setLoading(false);
            setError(null);

          } catch (transformError) {
            console.error('[useDiscussionMessages] Error transforming messages:', transformError);
            setError({
              message: transformError.message,
              code: 'TRANSFORM_ERROR',
              details: transformError
            });
            setLoading(false);
          }
        },
        (snapshotError) => {
          console.error('[useDiscussionMessages] onSnapshot error:', snapshotError);

          // Categorize error for better user feedback
          let errorMessage = snapshotError.message;
          let errorCode = snapshotError.code;

          if (snapshotError.code === 'permission-denied') {
            errorMessage = 'You do not have permission to view this discussion.';
            errorCode = 'PERMISSION_DENIED';
          } else if (snapshotError.code === 'unavailable') {
            errorMessage = 'Network error. Please check your connection.';
            errorCode = 'NETWORK_ERROR';
          }

          setError({
            message: errorMessage,
            code: errorCode,
            details: snapshotError
          });
          setLoading(false);
        }
      );

      // Store unsubscribe function for cleanup
      unsubscribeRef.current = unsubscribe;

      // Cleanup function - CRITICAL for preventing memory leaks
      return () => {
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
      };

    } catch (setupError) {
      console.error('[useDiscussionMessages] Error setting up listener:', setupError);
      setError({
        message: setupError.message,
        code: 'SETUP_ERROR',
        details: setupError
      });
      setLoading(false);
    }

    // Re-run effect when these dependencies change
  }, [effectiveUserId, activeTripId, limitCount, orderDirection, autoLoad]);

  /**
   * Load more messages (pagination)
   *
   * Strategy: Use getDocs() for pagination rather than onSnapshot to avoid
   * conflicts with the real-time listener. The live listener stays focused
   * on the latest N messages, while pagination fetches older messages.
   *
   * Trade-off: Paginated messages are snapshots (not live), but this is
   * acceptable for historical messages that rarely change.
   */
  const loadMore = useCallback(async () => {
    // Validation checks
    if (!lastDoc) {
      console.warn('[useDiscussionMessages] loadMore called but no lastDoc available');
      return;
    }

    if (!hasMore) {
      console.warn('[useDiscussionMessages] loadMore called but no more messages available');
      return;
    }

    if (loading || loadingMore) {
      console.warn('[useDiscussionMessages] loadMore called while already loading');
      return;
    }

    if (!effectiveUserId || !activeTripId) {
      console.error('[useDiscussionMessages] loadMore called but missing required IDs');
      return;
    }

    setLoadingMore(true);
    setError(null);

    try {
      // Build query for next batch
      const messagesRef = collection(
        db,
        `users/${effectiveUserId}/trips/${activeTripId}/discussion_messages`
      );

      const q = query(
        messagesRef,
        orderBy('timestamp', queryOrderDirection),
        startAfter(lastDoc), // Start after the last document from previous load
        limit(limitCount)
      );

      // Fetch next batch (snapshot, not live)
      const snapshot = await getDocs(q);

      // Transform documents
      let newMessages = snapshot.docs.map(doc => {
        const data = doc.data();
        const normalizedText = data.text ?? data.content ?? '';
        return {
          id: doc.id,
          ...data,
          text: normalizedText,
          timestamp: data.timestamp?.toDate?.()?.toISOString() || null,
          isPending: !data.timestamp
        };
      });
      if (queryOrderDirection === 'desc') {
        newMessages = newMessages.reverse();
      }

      // In chat-style ascending display, older pages belong before current items.
      // In descending display, older pages belong after current items.
      setMessages(prevMessages =>
        queryOrderDirection === 'desc'
          ? [...newMessages, ...prevMessages]
          : [...prevMessages, ...newMessages]
      );

      // Update pagination state
      const newLastDoc = snapshot.docs[snapshot.docs.length - 1];
      setLastDoc(newLastDoc);
      setHasMore(snapshot.docs.length === limitCount);

      setLoadingMore(false);

    } catch (loadMoreError) {
      console.error('[useDiscussionMessages] Error loading more messages:', loadMoreError);
      setError({
        message: loadMoreError.message,
        code: loadMoreError.code || 'LOAD_MORE_ERROR',
        details: loadMoreError
      });
      setLoadingMore(false);
    }
  }, [lastDoc, hasMore, loading, loadingMore, effectiveUserId, activeTripId, limitCount, queryOrderDirection]);

  return {
    messages,
    loading,
    error,
    hasMore,
    loadMore,
    loadingMore // Additional state for pagination loading
  };
};

/**
 * Implementation Notes:
 *
 * 1. Real-Time Strategy:
 *    - onSnapshot provides push-based updates for latest messages
 *    - New messages appear instantly when posted by any user
 *    - No polling needed - Firestore handles the push notifications
 *
 * 2. Pagination Strategy (Hybrid Approach):
 *    - onSnapshot: Latest N messages (always live)
 *    - getDocs(): Older messages (snapshot, not live)
 *    - Rationale: Historical messages rarely change, live updates less critical
 *    - Benefit: Avoids query conflicts between pagination and live listener
 *
 * 3. Timestamp Handling:
 *    - serverTimestamp() may be null initially (pending server write)
 *    - Messages marked as isPending when timestamp is null
 *    - UI can show loading indicator or "Sending..." state
 *    - Once server writes timestamp, message updates via onSnapshot
 *
 * 4. Order Direction:
 *    - 'asc' (default): Oldest first, new messages at bottom (chat-style)
 *    - 'desc': Newest first, new messages at top (feed-style)
 *    - UI scrolling strategy depends on order direction
 *
 * 5. Memory Management:
 *    - useRef stores unsubscribe function to survive re-renders
 *    - Cleanup function always calls unsubscribe on unmount
 *    - Handles React 18 StrictMode (double mount/unmount)
 *    - Prevents memory leaks from abandoned listeners
 *
 * 6. Error Handling:
 *    - Permission errors: User doesn't have access to trip
 *    - Network errors: Offline or connectivity issues
 *    - Transform errors: Malformed data in Firestore
 *    - Setup errors: Invalid configuration
 *
 * 7. Offline Support:
 *    - Firestore handles offline automatically
 *    - Messages posted offline sync when reconnected
 *    - onSnapshot fires with local cache even when offline
 *    - UI should handle isPending state for offline messages
 *
 * 8. Performance Considerations:
 *    - Limit to 50-100 messages max for initial load
 *    - Use pagination for older messages
 *    - Consider virtualized list for rendering large message counts
 *    - Firestore charges read for each document in snapshot
 *
 * 9. effectiveUserId Integration:
 *    - Shared trips: effectiveUserId = trip owner's UID
 *    - Owned trips: effectiveUserId = current user's UID
 *    - Messages always stored under trip owner's Firestore path
 *    - See CLAUDE.md "Shared Trip Data Access Pattern" for details
 */
